import * as React from 'react';
import { Truck } from 'lucide-react';

import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { BarrePagination } from '../ui/pagination-bar';
import { formatLitres } from '../../lib/formats';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';
import { PastillePrevu } from './TableauRapport';
import { Chiffre, EncartControleRecettes, Montant, PastilleSignalement } from './VisuelsRapport';
import { libelleEffectif, montant, pluriel, signalements, titreSignalement } from './reportHelpers';
import { GROUPE_DONNES, GROUPE_PROPRES, familleGroupe } from './OperatingAccountReport';
import { BoutonMarques, CarteVoirTous, MAX_CARTES_TRANSPORTEURS } from './DetailTransporteurs';

/**
 * Synthèse par camion: le classement du mois, du meilleur au moins bon
 * résultat. Chaque camion porte une barre de résultat (vers la droite en vert
 * s'il gagne, vers la gauche en rouge s'il perd), ses rotations, sa capacité,
 * sa destination. Au-dessus, les sous-totaux de chaque groupe de flotte (un
 * camion pris en location se range sous son seul transporteur; le découpage
 * par marque s'ouvre dans une fenêtre); en bas, le TOTAL. Le classement se lit
 * par pages: le rang est celui de tout le parc, d'une page à l'autre.
 */

/** Barre de résultat centrée sur zéro, à l'échelle du plus grand écart. */
const BarreResultat = ({ valeur, max, plaque }) => {
  const n = montant(valeur);
  const largeur = max > 0 ? Math.min(100, (Math.abs(n) / max) * 100) : 0;
  return (
    <span
      className="relative flex h-2 w-full items-center"
      role="img"
      aria-label={`Résultat de ${plaque} : ${n < 0 ? 'perte' : n > 0 ? 'gain' : 'nul'}`}
    >
      <span className="absolute inset-0 rounded-full bg-muted" />
      <span className="absolute left-1/2 top-[-3px] h-[14px] w-px bg-border" aria-hidden="true" />
      {n !== 0 && (
        <span
          className={cn('absolute h-full', n > 0 ? 'left-1/2 rounded-r-full bg-success' : 'right-1/2 rounded-l-full bg-destructive')}
          style={{ width: `${largeur / 2}%` }}
        />
      )}
    </span>
  );
};

const FAMILLE_TRANSPORTEUR = 'Pris en location';
const estTransporteur = (groupe) => familleGroupe(groupe.label) === FAMILLE_TRANSPORTEUR;

/**
 * Les cartes à afficher: « Propres » et « Donnés en location » d'abord, les
 * transporteurs ensuite, le convoi non réparti en dernier. Au-delà de
 * `MAX_CARTES_TRANSPORTEURS` transporteurs, seuls les meilleurs résultats ont
 * leur carte; les autres sont dans la liste complète.
 */
export const cartesDesGroupes = (groupes) => {
  const parResultat = (a, b) => montant(b.subtotal?.result) - montant(a.subtotal?.result);
  const transporteurs = groupes.filter(estTransporteur);
  const trop = transporteurs.length > MAX_CARTES_TRANSPORTEURS;
  const montres = trop ? [...transporteurs].sort(parResultat).slice(0, MAX_CARTES_TRANSPORTEURS) : transporteurs;
  const fixes = groupes.filter((groupe) => !estTransporteur(groupe));
  const avant = fixes.filter((groupe) => groupe.label === GROUPE_PROPRES || groupe.label === GROUPE_DONNES);
  const apres = fixes.filter((groupe) => !avant.includes(groupe));
  return { cartes: [...avant, ...montres, ...apres], transporteurs, trop };
};

const GRILLE =
  'xl:grid xl:grid-cols-[2rem_minmax(13rem,1.6fr)_minmax(7rem,1fr)_4.5rem_repeat(3,minmax(6.5rem,1fr))_minmax(8rem,1.1fr)] xl:items-center xl:gap-x-4';

const RangeeCamion = ({ camion, rang, max }) => {
  const deficitaire = montant(camion.result) < 0;
  const alertes = signalements(camion);
  const famille = familleGroupe(camion.groupe);
  const precisions = [
    camion.capacity_liters ? formatLitres(camion.capacity_liters) : null,
    camion.destination || null,
  ].filter(Boolean);
  return (
    <li className={cn('border-b border-border/60 px-4 py-3 last:border-b-0', GRILLE)}>
      <span className="hidden text-sm tabular-nums text-muted-foreground xl:block">{rang}</span>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            <span className="mr-1.5 font-normal tabular-nums text-muted-foreground xl:hidden">{rang}.</span>
            {camion.tractor_plate || 'Camion sans plaque'}
            {camion.tank_plate && <span className="font-normal text-muted-foreground"> · {camion.tank_plate}</span>}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {camion.driver_name && <span>{camion.driver_name}</span>}
            {famille === FAMILLE_TRANSPORTEUR && camion.brand && <span>{camion.brand}</span>}
            {famille && (
              <span className="max-w-[14rem] truncate whitespace-nowrap rounded-full border border-border px-1.5 leading-4" title={camion.groupe}>
                {camion.groupe}
              </span>
            )}
            {deficitaire && (
              <Badge variant="destructive" className="px-1.5 py-0 text-[10px]">
                Déficitaire
              </Badge>
            )}
            {camion.estimated && <PastillePrevu />}
            {alertes.map((texte) => (
              <PastilleSignalement key={texte} texte={texte} titre={titreSignalement(camion, texte)} />
            ))}
          </div>
        </div>
        <span className="shrink-0 xl:hidden">
          <Montant valeur={camion.result} signe fort />
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground xl:mt-0">{precisions.join(' · ')}</p>
      <span className="hidden text-right text-sm tabular-nums xl:block">{camion.rotations || '—'}</span>
      <span className="hidden text-right text-sm xl:block">
        <Montant valeur={camion.revenue} />
      </span>
      <span className="hidden text-right text-sm xl:block">
        <Montant valeur={camion.charges} />
      </span>
      <span className="hidden text-right text-sm xl:block">
        <Montant valeur={camion.result} signe fort />
      </span>
      <span className="mt-2 block xl:mt-0">
        <BarreResultat valeur={camion.result} max={max} plaque={camion.tractor_plate} />
      </span>
      <p className="mt-2 flex flex-wrap gap-x-4 text-xs text-muted-foreground xl:hidden">
        <span>
          {camion.rotations || 0} {pluriel(camion.rotations, 'rotation', 'rotations')}
        </span>
        <span>
          Recettes <Montant valeur={camion.revenue} className="text-foreground" />
        </span>
        <span>
          Charges <Montant valeur={camion.charges} className="text-foreground" />
        </span>
      </p>
    </li>
  );
};

const SyntheseParCamion = ({ report }) => {
  const groupes = React.useMemo(
    () => (report?.groups || []).filter((groupe) => (groupe.trucks || []).length > 0),
    [report],
  );
  const camions = React.useMemo(
    () =>
      groupes
        .flatMap((groupe) => groupe.trucks.map((camion) => ({ ...camion, groupe: groupe.label })))
        .sort(
          (a, b) =>
            montant(b.result) - montant(a.result) || String(a.tractor_plate).localeCompare(String(b.tractor_plate), 'fr'),
        ),
    [groupes],
  );
  // Le classement se lit par pages; changer de mois ramène à la première.
  const pagination = usePagination(camions, { cle: 'classement', reinitialiser: report?.period?.label || '' });

  if (!report || groupes.length === 0) {
    return (
      <EmptyState
        icon={Truck}
        title="Aucun camion ce mois-ci"
        description={`Aucun camion n'a de rotation ni de charge enregistrée pour ${report?.period?.label || 'ce mois'}.`}
      >
        Choisissez un autre mois avec les flèches.
      </EmptyState>
    );
  }

  const max = Math.max(0, ...camions.map((c) => Math.abs(montant(c.result))));
  const total = report.total || {};
  const nb = total.trucks_count ?? camions.length;
  const deficitaires = camions.filter((c) => montant(c.result) < 0).length;
  const estime = camions.some((c) => c.estimated);
  const { cartes, transporteurs, trop } = cartesDesGroupes(groupes);

  return (
    <div className="space-y-4">
      <EncartControleRecettes rapport={report} />

      <section aria-label="Sous-totaux par groupe" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cartes.map((groupe) => {
          const st = groupe.subtotal || {};
          const n = st.trucks_count ?? groupe.trucks.length;
          return (
            <div key={groupe.label} className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 truncate text-sm font-semibold text-foreground" title={groupe.label}>
                  {groupe.label}
                </p>
                {estTransporteur(groupe) && <BoutonMarques groupe={groupe} />}
              </div>
              <p className="text-xs text-muted-foreground">
                Sous-total · {libelleEffectif(groupe) || `${n} ${pluriel(n, 'camion', 'camions')}`} · {st.rotations ?? 0} {pluriel(st.rotations, 'rotation', 'rotations')}
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <Chiffre label="Recettes" valeur={st.revenue} alignement="left" />
                <Chiffre label="Charges" valeur={st.charges} alignement="left" />
                <Chiffre label="Résultat" valeur={st.result} signe fort alignement="left" />
              </div>
            </div>
          );
        })}
        {trop && <CarteVoirTous groupes={transporteurs} />}
      </section>

      <section aria-labelledby="titre-classement" className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border/70 px-4 py-2.5">
          <h3 id="titre-classement" className="text-sm font-semibold text-foreground">
            Classement par résultat
          </h3>
          <p className="text-xs text-muted-foreground">
            Du meilleur au moins bon · {deficitaires} {pluriel(deficitaires, 'camion déficitaire', 'camions déficitaires')} sur {nb}
          </p>
        </header>
        <div
          className={cn(
            'hidden border-b border-border/70 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
            GRILLE,
          )}
          aria-hidden="true"
        >
          <span>N°</span>
          <span>Camion</span>
          <span>Capacité · destination</span>
          <span className="text-right">Rotations</span>
          <span className="text-right">Recettes</span>
          <span className="text-right">Charges</span>
          <span className="text-right">Résultat</span>
          <span className="text-center">Perte · gain</span>
        </div>
        <ol>
          {pagination.lignes.map((camion, index) => (
            <RangeeCamion
              key={camion.truck_id || `${camion.number}-${camion.tractor_plate}`}
              camion={camion}
              rang={pagination.premiere + index}
              max={max}
            />
          ))}
        </ol>
        <div className={cn('grid grid-cols-3 gap-x-4 gap-y-1.5 border-t-2 border-foreground/15 px-4 py-2.5 text-sm font-semibold', GRILLE)}>
          <span className="col-span-3 xl:col-span-3">
            {total.label || 'TOTAL'}{' '}
            <span className="text-xs font-normal text-muted-foreground">
              {nb} {pluriel(nb, 'camion', 'camions')}
            </span>
          </span>
          <span className="hidden text-right tabular-nums xl:block">{total.rotations ?? 0}</span>
          <Chiffre label="Recettes" valeur={total.revenue} className="xl:hidden" />
          <Chiffre label="Charges" valeur={total.charges} className="xl:hidden" />
          <Chiffre label="Résultat" valeur={total.result} signe fort className="xl:hidden" />
          <span className="hidden text-right xl:block">
            <Montant valeur={total.revenue} />
          </span>
          <span className="hidden text-right xl:block">
            <Montant valeur={total.charges} />
          </span>
          <span className="hidden text-right xl:block">
            <Montant valeur={total.result} signe fort />
          </span>
          <span className="hidden xl:block" />
        </div>
      </section>
      <BarrePagination pagination={pagination} nom="camions" className="mt-0" />

      {estime && (
        <p className="text-xs text-muted-foreground">
          Les charges marquées « Prévu » contiennent un montant estimé, pas encore payé.
        </p>
      )}
    </div>
  );
};

export default SyntheseParCamion;
